import React from 'react'
import type { ContractType, ContractVehicleType } from './types'
import { useDropdown } from './useDropdown'

interface ContractDropdownProps {
  selectedContract?: ContractType | null
  selectedContractVehicle?: ContractVehicleType | null
  notListed?: boolean
  freeformContract?: string | null
  handleFreeformContract: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleNotListedClick: () => void
  handleAddContractClick: (contract: ContractType) => void
  handleUnselectContract: () => void
}

const ContractDropdown: React.FC<ContractDropdownProps> = ({
  selectedContract = null,
  selectedContractVehicle = null,
  notListed = false,
  freeformContract = null,
  handleFreeformContract,
  handleNotListedClick,
  handleAddContractClick,
  handleUnselectContract
}) => {
  const {
    isOpen,
    containerRef,
    buttonRef,
    setItemRef,
    handleToggle,
    handleButtonKeyDown,
    handleItemKeyDown,
    close
  } = useDropdown()

  const placeholderText = 'Select a Contract'

  const contractName = (contract: ContractType): string => {
    return contract.expired === true ? `${contract.name} (expired)` : contract.name
  }

  const handleItemClick = (contract: ContractType): void => {
    handleAddContractClick(contract)
    close()
  }

  const handleUnselectClick = (): void => {
    handleUnselectContract()
    close()
  }

  const handleNotListedClickInternal = (): void => {
    handleNotListedClick()
    close()
  }

  const sortedContracts = [...(selectedContractVehicle?.contracts ?? [])].sort((a, b) => a.name.localeCompare(b.name))

  const isFreeformMode = notListed || (freeformContract != null && freeformContract.length > 0)

  if (isFreeformMode) {
    return (
      <div id='contract-dropdown' className='select-dropdown-container'>
        <label className='dropdown-label'>Contract</label>
        <input
          autoFocus
          placeholder='Please enter contract name'
          onChange={handleFreeformContract}
          onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault() }}
          className='not-listed-input'
          value={freeformContract ?? ''}
        />
      </div>
    )
  }

  const selectText = selectedContract != null ? contractName(selectedContract) : placeholderText
  const disabled = selectedContractVehicle == null

  return (
    <div id='contract-dropdown' className='select-dropdown-container' ref={containerRef}>
      <label className='dropdown-label'>Contract</label>
      <button
        ref={buttonRef}
        type='button'
        onClick={handleToggle}
        disabled={disabled}
        className='dropdown-button'
        onKeyDown={handleButtonKeyDown}
        aria-haspopup='listbox'
        aria-expanded={isOpen}
      >
        {selectText}
        <i className='fa-solid fa-sort disabled dropdown-icon' />
      </button>
      {isOpen && (
        <div className='select-dropdowns'>
          <ul role='listbox' aria-label='Contract'>
            <li
              ref={setItemRef(0)}
              role='option'
              aria-selected={selectedContract == null}
              tabIndex={-1}
              onClick={handleUnselectClick}
              onKeyDown={(e) => handleItemKeyDown(e, 0)}
            >
              {placeholderText}
            </li>
            {sortedContracts.map((c, i) =>
              <li
                key={c.id}
                ref={setItemRef(i + 1)}
                role='option'
                aria-selected={selectedContract?.id === c.id}
                tabIndex={-1}
                onClick={() => handleItemClick(c)}
                onKeyDown={(e) => handleItemKeyDown(e, i + 1)}
              >
                {contractName(c)}
              </li>
            )}
            <li
              ref={setItemRef(sortedContracts.length + 1)}
              role='option'
              aria-selected={false}
              tabIndex={-1}
              onClick={handleNotListedClickInternal}
              onKeyDown={(e) => handleItemKeyDown(e, sortedContracts.length + 1)}
              className='not-listed-option'
            >
              <i className='fa-solid fa-do-not-enter' />
              I don't see my contract
            </li>
          </ul>
        </div>
      )}
    </div>
  )
}

export { ContractDropdown }
